import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ButtonItem from '../components/ButtonItem';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import SectionTabs from '../components/SectionTabs';
import { Building2, Mic2, Disc3, Music, Package } from 'lucide-react';
import SongsTable from '../components/SongsTable';

function SongsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPage = parseInt(params.get('page')) || 1;
  const initialSearch = params.get('search') || '';

  const [loading, setLoading] = useState(false);
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchKeyword, setSearchKeyword] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);


  const fetchSongs = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/song`,
        {
          params: {
            page,
            limit: 10,
            search,
          },
        }
      );
      setSongs(response.data.items);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('서버 접속 에러:', error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchSongs();
  // }, []);

  useEffect(() => {
    fetchSongs(currentPage, searchKeyword);
  }, [currentPage, searchKeyword]);

  // const handlePageChange = (newPage) => {
  //   fetchSongs(newPage, searchTerm);
  // };

  const handlePageChange = (newPage) => {
    fetchSongs(newPage, searchTerm);
    setCurrentPage(newPage);
    const searchParam = searchKeyword ? `&search=${encodeURIComponent(searchKeyword)}` : '';
    navigate(`/Songs?page=${newPage}${searchParam}`);
  };

  const handleDownload = async () => {
    try {
      const params = { search: searchTerm, page: 1, limit: totalCount };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/song`,
        { params }
      );
      const allSongs = response.data.items;

      const exportSongs = allSongs.filter((s) => s.nameKr.includes(searchTerm));

      const exportData = exportSongs.map((s, idx) => {
        return {
          곡코드: s.code,
          곡명: s.nameKr,
          앨범명: s.album?.nameKr || '',
          아티스트명: s.album?.artist?.nameKr || '',
          권리사: s.clientDisplay || '',
          발매일: s.album?.releaseDateTime || '',
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '곡');
      XLSX.writeFile(wb, `곡.xlsx`);
    } catch (error) {
      console.error('엑셀 다운로드 중 에러:', error);
    }
  };

  const dataTabs = [
    { label: '권리사', to: '/Clients', icon: Building2 },
    { label: '아티스트', to: '/Artists', icon: Mic2 },
    { label: '앨범', to: '/Albums', icon: Disc3 },
    { label: '곡', to: '/Songs', icon: Music },
    { label: '음반', to: '/PhysicalAlbums', icon: Package },
  ];

  return (
    <>
        <div className="flex mb-8 justify-center items-center">
          <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            <span>곡 리스트</span>
          </div>
        </div>
        <SectionTabs items={dataTabs} />

        <SearchInput
          placeholder="곡명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1);
            setSearchKeyword(searchTerm);
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            navigate(`/Songs?page=1${searchParam}`);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-400">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <SongsTable
              data={songs}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={10}
              searchKeyword={searchKeyword}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            <div className="w-full flex justify-end items-center mt-6">
              <ButtonItem
                text="등록"
                color="blue"
                onClick={() => navigate('/SongWrite')}
              />
              <button
                onClick={handleDownload}
                className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors ml-2">
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                다운로드
              </button>
            </div>
          </>
        )}
    </>
  );
}

export default SongsPage;
